import { useMemo, useCallback } from 'react'
import { Button, Table, Tag, Tooltip } from 'antd'
import {
  CheckOutlined,
  FileTextFilled,
  MinusOutlined,
  FileSearchOutlined,
  FileExcelOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import dayjs from '../../lib/datetime'
import Filters from '../Forms/Filters'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'
import { ListError } from '../Common'
import { PAGE_SIZE_OPTIONS, PAGE_SIZE } from '../../lib/constants'
import { toPath } from '../../routes'
import { useAsync } from '../../hooks'
import useExportExcel from '../../hooks/useExportExcel'
import { useFetchOrders, useOrders } from '../../redux/slices/orders'
import ShowTotal from '../Common/ShowTotal'

const useStyles = createUseStyles({
  present: {
    color: 'green',
  },
  notPresent: {
    color: 'orange',
  },
  ok: {
    color: '#2b8a3e',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start', // Changed from flex-end to flex-start
    justifyContent: 'space-between', // Changed from flex-start to space-between
  },
  exportContainer: {
    textAlign: 'center',
  },
})

const DEFAULT_FILTERS = {
  procedureMatch: 'all',
  showDuplicates: false,
}

const DEFAULT_PAGE = {
  fulfilledPage: 1,
  fulfilledPageSize: PAGE_SIZE,
}

export default function FulfilledOrders({ cycleId, activeTab }) {
  const classes = useStyles() // Call the hook to get class names

  const columns = [
    {
      title: 'Patient Name',
      dataIndex: ['patient'],
      render: (_, { patient }) =>
        patient && patient.id ? (
          <Link to={toPath.patients.patient({ id: `${patient.id}` })}>
            {patient.lastName}, {patient.firstName}
          </Link>
        ) : (
          <>No Patient</>
        ),
    },
    {
      title: 'Patient MRN',
      align: 'right',
      dataIndex: ['patient', 'practiceMrn'],
    },
    {
      title: 'Order Number',
      align: 'right',
      dataIndex: ['orderNumber'],
    },
    {
      title: 'Device Type',
      dataIndex: ['deviceType'],
      render: (_, { deviceType }) => deviceType || <MinusOutlined />,
    },
    {
      title: 'ICM Active',
      align: 'center',
      dataIndex: ['icmActive'],
      render: (_, { icmActive }) => {
        if (icmActive === true) return 'Yes'
        if (icmActive === false) return 'No'
        return <MinusOutlined />
      },
    },
    {
      title: 'Procedure Match',
      align: 'center',
      dataIndex: ['orderStatus'],
      render: (_, { orderStatus }) => {
        if (orderStatus === 'mismatched')
          return <Tag color="warning">Wrong Order</Tag>
        if (orderStatus === 'matched')
          return (
            <span className={classes.ok}>
              <CheckOutlined /> OK
            </span>
          )
        if (orderStatus === 'missing_pre_auth')
          return <Tag>No Pre-Auth</Tag>
        return <MinusOutlined />
      },
    },
    {
      title: 'Encounter',
      align: 'right',
      dataIndex: ['encounterId'],
    },
    {
      title: 'Created At',
      dataIndex: ['createdAt'],

      render(text, { createdAt }) {
        return <div>{dayjs(createdAt).format('MMM D, YYYY')}</div>
      },
    },
    {
      title: 'Order Attributes',
      align: 'center',
      dataIndex: ['requestData'],
      render: (_, { requestData }) => {
        if (!requestData) return <MinusOutlined />

        // Format the JSON data for display
        const formattedData = JSON.stringify(requestData, null, 2)

        return (
          <Tooltip
            title={
              <pre
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formattedData}
              </pre>
            }
            overlayStyle={{ maxWidth: '600px' }}
            placement="right"
          >
            <FileTextFilled style={{ cursor: 'pointer' }} />
          </Tooltip>
        )
      },
    },

    {
      title: 'Report',
      key: 'actions',
      render(_text, { remoteCheckId }) {
        return (
          <span>
            <Link to={`/remote-checks/${remoteCheckId}`}>
              <Tooltip
                title={remoteCheckId ? 'View Remote Check' : 'No Remote Check'}
              >
                <Button
                  disabled={!remoteCheckId}
                  type="link"
                  icon={<FileSearchOutlined />}
                />
              </Tooltip>
            </Link>
          </span>
        )
      },
    },
  ]

  // eslint-disable-next-line no-unused-vars
  const [_, { practice, practiceId }] = usePracticeCycles()

  const { conditions, setConditions } = useFilters({
    ...DEFAULT_FILTERS,
    practiceId,
  })

  const { fulfilledPage, fulfilledPageSize } = {
    ...DEFAULT_PAGE,
    ...conditions,
  }

  // this is a general implementation, among them are making sure some keys are
  // on the expected format and to avoid re-render if a key changes in
  // `conditions` but it don't belong to FULFILLED_FILTERS array.
  const memoConditions = useMemo(() => {
    const {
      currentPracticeId,
      deviceTypeId,
      medicalProviderId,
      officeLocationId,
      procedureMatch,
      query,
      showDuplicates,
    } = conditions

    return {
      currentPracticeId,
      cycleId,
      deviceTypeId,
      medicalProviderId,
      officeLocationId,
      practiceId,
      procedureMatch,
      query,
      // XXX: this is to avoid type warnings when loading queryParams
      showDuplicates: showDuplicates === true || showDuplicates === 'true',
    }

    // NOTE: eslint want to check `conditions` because of pick function, but
    // it's only necessary to refresh `memoConditions` when the ones below are
    // changed:
    //
  }, [conditions, cycleId, practiceId])

  const { error, clearError, pending } = useAsync(
    useFetchOrders(conditions, true),
  )

  const { orders, total } = useOrders(conditions)

  const setFilters = useCallback(
    newConditions =>
      setConditions({
        ...memoConditions,
        ...DEFAULT_FILTERS,
        ...newConditions,
        fulfilledPage: 1, // reset page to the first as all records changed
        fulfilledPageSize,
        activeTab,
      }),
    [setConditions, memoConditions, fulfilledPageSize, activeTab],
  )

  const setPage = useCallback(
    (newPage, newPageSize) =>
      setConditions({
        ...memoConditions,
        ...DEFAULT_PAGE,
        activeTab,
        fulfilledPage: newPage,
        fulfilledPageSize: newPageSize,
      }),
    [activeTab, memoConditions, setConditions],
  )

  const setPageSize = useCallback(
    (newPage, newPageSize) =>
      setConditions({
        ...memoConditions,
        activeTab,
        fulfilledPage: newPage,
        fulfilledPageSize: newPageSize,
      }),
    [activeTab, memoConditions, setConditions],
  )

  const excelExportUrl = useMemo(
    () =>
      queryString.stringifyUrl({
        url: `/reports/orders/`,
        query: {
          cycle_id: cycleId,
          device_type_id: memoConditions.deviceTypeId,
          medical_provider_id: memoConditions.medicalProviderId,
          office_location_id: memoConditions.officeLocationId,
          show_duplicates: memoConditions.showDuplicates,
          q: memoConditions.query,
        },
      }),
    [cycleId, memoConditions],
  )

  const [exportingExcel, exportExcel] = useExportExcel(excelExportUrl)

  const showTotal = () => <ShowTotal orders={orders} total={total} />

  return (
    <div>
      <div className={classes.container}>
        {error && (
          <ListError description={error.message} onClose={clearError} />
        )}

        <Filters
          defaultValues={DEFAULT_FILTERS}
          currentValues={memoConditions}
          onSubmit={setFilters}
          onChange={setFilters}
          placeholders={{ query: 'Search Order or Patient' }}
          showDeviceType
          showDuplicatesCheck
          showMedicalProvider
          showOfficeLocation
          showProcedureMatch
          showSearch
        />

      </div>
      <Table
        rowKey="id"
        columns={columns}
        className={classes.ordersTable}
        dataSource={orders}
        loading={pending}
        pagination={{
          total,
          page: +fulfilledPage,
          pageSize: +fulfilledPageSize,
          onChange: setPage,
          onShowSizeChange: setPageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showSizeChanger: true,
          showTotal,
        }}
      />
      {Boolean(orders.length) && (
        <div className={classes.exportContainer}>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}

FulfilledOrders.propTypes = {
  cycleId: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
}
